import { useCallback, useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import ChatPresentation from 'chat-ui/ChatPresentation';
import { useThemeQuery } from 'chat-ui/src/reactQuery/get';
import useChatStore from 'chat-ui/src/store';
import { toast } from 'react-toastify';
import useChatSettingsStore from 'chat-ui/src/ChatSettingsStore';
import { getErrorStatus } from '@common/utils';
import { EProjectStatus, EStatusCode } from '@/enums';
import { getTestChatHistory, testChatRegister, axiosInstanceChat } from './axios/axiosInstanceChat';
import { ChatProps } from '@/types';
import { flattenGroupToQuestions } from '@/utils';

const chatCannotRegisterError =
  'We cannot find this chat. Please check the link or contact the owner of the chat.';
const errorSendingMessage = "We can't send your message. Please try again.";
const incompleteChatSetupWarning =
  'Please ensure that both the opening message and the first question are filled out before initiating the test conversation.';
const incompleteEmbedSetupWarning =
  'Please ensure the first question is filled out before initiating the test conversation.';

function DashboardTestChat({ projectId, chatHeight, singleProjectData }: ChatProps) {
  const {
    setInitialMessages,
    setSeen,
    addErrorMessage,
    setRefreshChatHistory,
    currentConversationId,
    setCurrentConversationId,
  } = useChatStore((state) => state);

  const [isRefreshLoading, setIsRefreshLoading] = useState(false);
  // TELV1-571 (PR review) — the effects below guard staleness with a
  // per-effect `cancelled` flag, but handleRestartChat is a useCallback and
  // has no teardown to hang one on. This ref answers the same question:
  // "is the project this work started for still the one on screen?"
  const currentProjectIdRef = useRef(projectId);
  currentProjectIdRef.current = projectId;
  const { setChatSettingsData } = useChatSettingsStore();

  const { data: themeData } = useThemeQuery(projectId);

  const hasOpeningMessage = !!singleProjectData?.opening_message;

  // Use existing helper to flatten all questions from nested groups and check if any have content
  const hasFirstQuestion = singleProjectData?.interview_questions
    ? flattenGroupToQuestions(singleProjectData.interview_questions).some(
        (q) => q.question && q.question.trim() !== '',
      )
    : false;
  // TELV1-186 — embed projects do not require an opening message; only a first
  // question is needed to start a test conversation (mirrors the embed-aware
  // publish-readiness + backend @ValidateIf for embed).
  const isEmbedProject = singleProjectData?.project_type === 'embed';
  const isSetupComplete = hasFirstQuestion && (isEmbedProject || hasOpeningMessage);
  const typeOfInterview = singleProjectData?.type_of_interview;
  const isProjectCompleted = singleProjectData?.status === EProjectStatus.COMPLETED;
  /**
   * TELV1-571 — `isCancelled` is what stops a register that was fired for a
   * project the user has since navigated away from writing its conversation
   * id into the (module-global, not per-project) chat store. Without it the
   * clearing effect below runs first, the late resolve repopulates the
   * store, and the previous project's transcript renders under the new
   * project's title.
   */
  const handleChatRegister = useCallback(
    async ({
      onError,
      isCancelled,
    }: { onError?: () => void; isCancelled?: () => boolean } = {}) => {
      try {
        const data = await testChatRegister(projectId);
        if (isCancelled?.()) return;
        if (data?.conversationId && data?.type_of_interview) {
          setCurrentConversationId?.(data?.conversationId);
          setChatSettingsData(data?.type_of_interview as string[]);
        }
      } catch (error) {
        if (isCancelled?.()) return;
        onError?.();
      }
    },
    [projectId, setCurrentConversationId, setChatSettingsData],
  );

  const getHistoryAndSetInitMsg = useCallback(
    async (isCancelled?: () => boolean) => {
      if (!currentConversationId) return;
      try {
        const { messages } = await getTestChatHistory(currentConversationId);
        if (isCancelled?.()) return;
        setInitialMessages(messages);
        setSeen();
      } catch (error) {
        // TELV1-307 — axiosInstanceChat unwraps `error.response`, so
        // reading `axiosError.response?.status` inline returned undefined
        // and the INVALID_CREDENTIALS guard never short-circuited the
        // error-message dispatch.
        if (isCancelled?.()) return;
        const axiosError = error as AxiosError;
        if (axiosError && getErrorStatus(axiosError) !== EStatusCode.INVALID_CREDENTIALS) {
          addErrorMessage(errorSendingMessage);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentConversationId],
  );

  useEffect(() => {
    // Same staleness guard as the automatic path: a manual refresh that
    // resolves after the user has moved on must not write into the store.
    const requestedConversationId = currentConversationId;
    setRefreshChatHistory(() =>
      getHistoryAndSetInitMsg(() => requestedConversationId !== currentConversationId),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId]);

  const handleRestartChat = useCallback(async () => {
    setIsRefreshLoading(true);
    try {
      if (currentConversationId) {
        const requestedProjectId = projectId;
        const isStale = () => currentProjectIdRef.current !== requestedProjectId;

        const response = await axiosInstanceChat.post(
          `${import.meta.env.VITE_CHAT_URL}/chat/reset/${projectId}/${currentConversationId}`,
          undefined,
        );

        // Re-register the chat to fetch the latest project data (including updated questions)
        await handleChatRegister({ isCancelled: isStale });

        setIsRefreshLoading(false);
        // The reset response belongs to the project the refresh started on.
        // Writing it after a project switch is the same leak the register
        // race caused, just user-triggered.
        if (isStale()) return;
        setInitialMessages(response?.data?.messages);
      } else {
        setIsRefreshLoading(false);
        // Embed studies have no opening message — only the first question is
        // required, so the warning should not mention an opening message.
        toast.info(isEmbedProject ? incompleteEmbedSetupWarning : incompleteChatSetupWarning);
      }
    } catch (error) {
      setIsRefreshLoading(false);
      addErrorMessage(errorSendingMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, currentConversationId, handleChatRegister]);

  useEffect(() => {
    // The cleanup flips `cancelled` when projectId (or the project's
    // readiness) changes, so an in-flight register belonging to the previous
    // project is discarded instead of landing in the shared store.
    // Readiness is embed-aware (TELV1-186): embed studies have no opening
    // message, so isSetupComplete gates on first question only for them.
    let cancelled = false;
    if (isSetupComplete && projectId) {
      handleChatRegister({
        onError: () => addErrorMessage(chatCannotRegisterError),
        isCancelled: () => cancelled,
      });
    } else {
      // Still needed on top of ProjectCreate's pathname cleanup: this covers
      // the researcher emptying the opening message or the last question on
      // the SAME project, where the route never changes.
      setCurrentConversationId('');
      setInitialMessages([]);
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSetupComplete, projectId]);

  useEffect(() => {
    if (!currentConversationId) return undefined;
    let cancelled = false;
    getHistoryAndSetInitMsg(() => cancelled);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId]);

  useEffect(() => {
    setChatSettingsData(typeOfInterview as string[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeOfInterview, projectId]);

  return (
    <ChatPresentation
      chatHeight={chatHeight}
      handleRestartChat={handleRestartChat}
      restartChatButton
      isTestChat
      isRefreshLoading={isRefreshLoading}
      themeData={themeData}
      isProjectCompleted={isProjectCompleted}
    />
  );
}

export default DashboardTestChat;
